import { useState } from 'react';
import type { AnnouncementItem } from '@/lib/announcementQueries';
import { LinkifiedText } from '@/components/LinkifiedText';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

/**
 * The announcement in full — image included.
 *
 * Shared by the dashboard and the Announcements page, which both show a small
 * image in the row and open this to see it properly. Two copies of the same
 * dialog is two places for the image handling to drift apart, and the image
 * handling is the part with a real failure mode (see `AnnouncementImage`).
 */

const PRIORITY_VARIANT = {
  URGENT: 'destructive',
  HIGH: 'warning',
  LOW: 'outline',
  NORMAL: 'outline',
} as const;

export function AnnouncementDialog({
  announcement,
  onClose,
}: {
  announcement: AnnouncementItem | null;
  onClose: () => void;
}) {
  const [imageFailed, setImageFailed] = useState(false);

  return (
    <Dialog
      open={Boolean(announcement)}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
          setImageFailed(false);
        }
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        {announcement && (
          <>
            <DialogHeader>
              {announcement.priority !== 'NORMAL' && (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={PRIORITY_VARIANT[announcement.priority]}>
                    {announcement.priority.charAt(0) +
                      announcement.priority.slice(1).toLowerCase()}
                  </Badge>
                </div>
              )}
              {/* Linkified, for the same reason as the body beneath it — a
                  meeting link pasted as the title has to be clickable. */}
              <DialogTitle className="text-left">
                <LinkifiedText text={announcement.title} />
              </DialogTitle>
              <DialogDescription className="text-left">
                {announcement.createdBy.firstName} {announcement.createdBy.lastName}
                {announcement.publishedAt && (
                  <>
                    {' · '}
                    {new Date(announcement.publishedAt).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>

            {/* The body is optional — a poster can be the whole announcement.
                Rendering it regardless left an empty paragraph holding the
                dialog's gap above the picture. */}
            {announcement.body.trim() && (
              <LinkifiedText text={announcement.body} className="block text-sm leading-relaxed" />
            )}

            {announcement.mediaUrl && !imageFailed && (
              <img
                src={announcement.mediaUrl}
                alt={`Image attached to ${announcement.title}`}
                onError={() => setImageFailed(true)}
                className="w-full rounded-lg border object-contain"
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * The attached image as a small tile, or nothing if it will not load.
 *
 * The `src` is a signed asset link carrying an expiry, so a tab left open long
 * enough reaches a URL the server refuses — and an `<img>` with a dead src
 * renders a broken-image icon rather than failing quietly. Removing the element
 * is the honest outcome; the text of the announcement is unaffected.
 */
export function AnnouncementThumb({
  url,
  title,
  className,
  onClick,
}: {
  url: string;
  title: string;
  className?: string;
  onClick?: () => void;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  const image = (
    <img
      src={url}
      alt={`Image attached to ${title}`}
      onError={() => setFailed(true)}
      loading="lazy"
      /*
        ── `contain`, NOT `cover` ────────────────────────────────────────────

        These attachments are screenshots, posters and illustrations, not
        portraits. `object-cover` fills the 56px square by cropping to the
        middle of whatever it is given — which on a wide screenshot is a
        meaningless strip of its centre, and on an illustration cuts the
        subject's head off. The tile looked broken because it was showing the
        middle of a picture instead of the picture.

        `contain` fits the whole image inside the tile and letterboxes the
        rest against the muted ground, so a glance tells you WHICH attachment
        it is — which is the only thing a thumbnail this size can do.

        `p-0.5` keeps the artwork off the border, so a light image does not
        appear to bleed into the frame.
      */
      className="h-full w-full rounded-lg border bg-muted object-contain p-0.5"
    />
  );

  if (!onClick) return <div className={className}>{image}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className ?? ''} group/img relative overflow-hidden rounded-lg`}
      aria-label={`Open ${title} full size`}
    >
      {image}
      <span className="absolute inset-0 rounded-lg bg-black/0 transition-colors group-hover/img:bg-black/10" />
    </button>
  );
}
